import { z } from 'zod';

/**
 * 节点树：组织单元 + 挂在它们上面的资产。
 *
 * 一棵树、一个 parentId。「归属」就是父——某个 ATC 归哪个部门，是沿父链上溯的一次查询，
 * 不是一个字段，所以永远不会和树对不上。
 *
 * 类型有两个来源，这是这套模型唯一的复杂之处：
 *
 * - **组织单元类型**（公司 / 部门 / 班组）来自数据库，客户能改名、能加。
 * - **资产类型**（目前只有 ATC）来自代码，客户只能用，不能改——它是开发出来的。
 *
 * 两者在 `nodeTypes()` 里合流成同一个形状，上层（树、表单、校验）只认合并后的形状，
 * 永远不知道来源。这是「一棵树一套逻辑」在代码层面成立的办法，否则到处都是 `if`。
 *
 * 代价说清楚：`Node.typeCode` 因此不能有外键——它可能指向一个没有数据库行的代码类型。
 * 合法性由合并后的类型表校验，不由数据库保证。
 */

/* ═══════ 标识与基础字段 ═══════ */

export const nodeTypeCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z][A-Z0-9_]{1,23}$/, '2–24 位大写英文、数字或下划线，首位必须是字母');

export const nodeNameSchema = z.string().trim().min(1, '请填写名称').max(60);

/** 节点的可选编码，同一个父节点下不重复。 */
export const nodeCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9][A-Z0-9_-]{0,31}$/, '1–32 位大写英文、数字、下划线或连字符');

/** 组织单元还是资产。就是界面上「显示类别」那个开关要显示的东西。 */
export const nodeKindSchema = z.enum(['ORG_UNIT', 'ASSET']);
export type NodeKind = z.infer<typeof nodeKindSchema>;

/** 类型定义存在哪儿。`config` 在数据库，`code` 在代码里。 */
export const typeOriginSchema = z.enum(['config', 'code']);
export type TypeOrigin = z.infer<typeof typeOriginSchema>;

/* ═══════ 节点类型 ═══════ */

export const nodeTypeSchema = z.object({
  /** 稳定标识，也是主键。永不改——数据库里存的就是它。 */
  code: z.string(),
  name: z.string(),
  kind: nodeKindSchema,
  origin: typeOriginSchema,
  /** 能不能直接挂在根（企业）下。单公司的客户不该被迫先建一个没意义的「公司」。 */
  allowUnderRoot: z.boolean(),
  /**
   * 明确列举「我能放在哪些类型下面」。
   *
   * 注意：资产类型**不该**用它来列举组织单元类型——组织单元类型是客户能加的，
   * 代码里枚举客户的自由必然对不上（客户加一个「工段」，所有资产就都挂不上去）。
   * 那种情况用下面那个能力位。
   */
  parentTypeCodes: z.array(z.string()),
  /** 可以挂在**任何**组织单元下，包括客户以后新加的那些。 */
  underAnyOrgUnit: z.boolean(),
  /** 叶子：它下面不能再挂东西。ATC 就是叶子。 */
  leaf: z.boolean(),

  sortOrder: z.number(),
  /** 有多少节点在用它。 */
  nodeCount: z.number(),
  /** 能不能改名。代码类型不能。 */
  canRename: z.boolean(),
  /** 能不能删。代码类型不能；配置类型要没有节点在用。 */
  canRemove: z.boolean(),
});

export type NodeType = z.infer<typeof nodeTypeSchema>;

/* ═══════ 节点 ═══════ */

export const treeNodeSchema = z.object({
  id: z.string(),
  typeCode: z.string(),
  /** null 表示挂在根（企业）下。企业本身不是一条节点记录——它已经是单例了。 */
  parentId: z.string().nullable(),
  name: z.string(),
  code: z.string().nullable(),
  sortOrder: z.number(),
  /** 停用而不是删除：节点一旦被别处引用，真删就留下悬空引用。 */
  enabled: z.boolean(),
  /**
   * 这个类型自己的字段。扩展点就在这里——以后加物理资产或别的数字资产，
   * 是加一个类型加一份属性校验，不动表结构、不写迁移。
   */
  attributes: z.record(z.string(), z.unknown()),
  /** 直接挂在这个节点上的人数。资产上没有人。 */
  memberCount: z.number(),
});

export type TreeNode = z.infer<typeof treeNodeSchema>;

/**
 * 整棵树一次返回。
 *
 * 不分页：几千个节点不值得，而且前端自己拼树就省掉了「展开一层拉一次」的往返和加载态。
 * 也正因为整棵都在手上，SQL 层从来不需要「查子树」，所以不存物化路径和 depth——
 * 那两个是派生值，在内存里拼，少两份要维护的真相。
 */
export const nodeTreeSchema = z.object({
  types: z.array(nodeTypeSchema),
  nodes: z.array(treeNodeSchema),
});

export type NodeTree = z.infer<typeof nodeTreeSchema>;

/* ═══════ 层级规则：只有这一份实现 ═══════ */

/**
 * child 能不能放在 parent 下面。
 *
 * 前端拿它灰掉不合规的目标，后端拿它拒绝请求——**必须是同一个函数**。
 * 两份实现必然漂移，而且那种漂移最难发现：界面允许了，接口拒绝了。
 */
export function canPlaceUnder(child: NodeType, parent: NodeType | null): boolean {
  if (parent === null) return child.allowUnderRoot;
  // 叶子下面不能挂东西
  if (parent.leaf) return false;
  if (child.underAnyOrgUnit && parent.kind === 'ORG_UNIT') return true;
  return child.parentTypeCodes.includes(parent.code);
}

/** 在某个父节点下，哪些类型可以新建。父为 null 表示根。 */
export function allowedChildTypes(types: NodeType[], parent: NodeType | null): NodeType[] {
  return types.filter((t) => canPlaceUnder(t, parent));
}

/* ═══════ 三层固定的类型（C-077 · ADR-0020 六） ═══════ */

/**
 * **三层就是这三个，写死在代码里。**
 *
 * 此前这里只有 ATC 一条，组织单元类型由客户在 `NodeType` 表里自己建，
 * 层级形状由 `NodeTypeParent` 配。那套设计有一个致命处：
 * **它把「层的身份」交给了客户去配**，而 I-1 约束的恰恰是层的身份
 * （ADR-0020 二：违反 I-1 的是层的身份失控）。
 *
 * 具体到当时的常量：ATC 写着 `allowUnderRoot: true` 与 `underAnyOrgUnit: true`
 * ——**「ATC 挂在企业下」「ATC 挂在任意组织单元下」是被明文允许的**，
 * `tree.test.ts` 里三条通过用例把它固化了下来。那不是实现疏漏，是设计如此。
 *
 * 现在三层由外键决定（`position.department_id`、`atc.position_id` 均 NOT NULL FK），
 * 类型表只负责显示。**客户仍能改这三层的叫法**（`NodeType.name` 覆盖 `name`），
 * 但改不了它们的层级关系——这就是 ADR-0020 六说的「层级定义权退役，只保留显示命名」。
 */
export const CODE_NODE_TYPES: readonly Omit<NodeType, 'nodeCount' | 'canRename' | 'canRemove'>[] = [
  {
    code: 'DEPARTMENT',
    name: '部门',
    kind: 'ORG_UNIT',
    origin: 'code',
    /** 部门直接挂在企业下。**部门之下不再有部门**——只取一级职能部门（ADR-0048）。 */
    allowUnderRoot: true,
    parentTypeCodes: [],
    underAnyOrgUnit: false,
    leaf: false,
    sortOrder: 10,
  },
  {
    code: 'POSITION',
    name: '岗位',
    kind: 'ORG_UNIT',
    origin: 'code',
    allowUnderRoot: false,
    /** 只能挂在部门下。库级 NOT NULL FK 保的是同一条。 */
    parentTypeCodes: ['DEPARTMENT'],
    underAnyOrgUnit: false,
    leaf: false,
    sortOrder: 20,
  },
  {
    code: 'ATC',
    name: 'ATC',
    kind: 'ASSET',
    origin: 'code',
    /** **不再允许挂在企业下。** 全公司通用的标准归系统岗位承载（ADR-0020 三）。 */
    allowUnderRoot: false,
    /** **只能挂岗位。** 这一行就是 I-1 在契约层的表达。 */
    parentTypeCodes: ['POSITION'],
    /** **不再是「任意组织单元都行」。** 那句话本身就是 I-1 的反面。 */
    underAnyOrgUnit: false,
    leaf: true,
    sortOrder: 30,
  },
] as const;

/** 三层的编码。用到「这是不是固定层」的地方都引它，不要各处写字符串。 */
export const FIXED_TYPE_CODES = ['DEPARTMENT', 'POSITION', 'ATC'] as const;
export type FixedTypeCode = (typeof FIXED_TYPE_CODES)[number];

/* ═══════ 写操作 ═══════ */

export const createNodeSchema = z.object({
  typeCode: nodeTypeCodeSchema,
  parentId: z.string().nullable(),
  name: nodeNameSchema,
  code: nodeCodeSchema.optional(),
  attributes: z.record(z.string(), z.unknown()).optional(),
});

export type CreateNode = z.infer<typeof createNodeSchema>;

export const updateNodeSchema = z.object({
  name: nodeNameSchema.optional(),
  /** 空字符串表示清掉编码。 */
  code: z.union([nodeCodeSchema, z.literal('')]).optional(),
  enabled: z.boolean().optional(),
  attributes: z.record(z.string(), z.unknown()).optional(),
});

export type UpdateNode = z.infer<typeof updateNodeSchema>;

export const moveNodeSchema = z.object({
  /** null = 移到根下。目标不能在自己的子树里，否则那一支会从树上掉下来。 */
  parentId: z.string().nullable(),
});

export const reorderNodesSchema = z.object({
  /** 同一个父节点下的全部子节点，按想要的顺序排好。 */
  parentId: z.string().nullable(),
  orderedIds: z.array(z.string()).min(1),
});

/* ═══════ 层的称谓（层级定义权已退役，见 CODE_NODE_TYPES） ═══════ */

/**
 * 改这一层的叫法。**只有 name。**
 *
 * 以前这里还有 `createNodeTypeSchema`（建类型）与 `updateNodeTypeSchema`
 * 的 `allowUnderRoot` / `parentTypeCodes`——那三样是「由客户决定树有几层、
 * 哪层放哪层下」的入口，已随 ADR-0020 六退役。
 *
 * **删掉字段而不是留着忽略**：留着的话，客户端传了 `parentTypeCodes` 会被静默丢弃，
 * 界面上看起来配成功了、实际没生效。删掉之后同样的请求会在 400 里被指出来。
 */
export const renameNodeTypeSchema = z
  .object({
    name: nodeNameSchema,
  })
  /**
   * **`.strict()` 不能省。**
   *
   * zod 默认**剥掉**多余字段——那样传 `parentTypeCodes` 会被静默丢弃，
   * 界面上看着配成功了、实际没生效，而且不报错。这正是这次退役要避免的坏法。
   * 测试里那条「给了也不认——不是被忽略，是 400」第一次跑就红了，
   * 说明少了这一行时上面那句注释是假的。
   */
  .strict();

export type RenameNodeType = z.infer<typeof renameNodeTypeSchema>;

/** 把某个成员挂到某个节点上；null 表示不属于任何节点。 */
export const assignMemberNodeSchema = z.object({
  nodeId: z.string().nullable(),
});
